// Shared source metadata for graduate-outcomes data origins.
//
// Both EmployerDetail (per-employer feeder table) and UniversityDetail
// (per-uni Top-employers card) show a provenance pill explaining
// where the employer edges came from. The underlying tag lives on
// each degree document as `outcomes.employer_edges_source` (stamped
// by `scripts/backfill_employer_edges_source.py` using one canonical
// label per country).
//
// This module centralises:
//   • SOURCE_META   — tag → {label, note} for all 12 tagged countries
//   • labelForTag   — safe lookup (falls back to prettified tag)
//   • tagForCountry — country-code / country-name → canonical tag
//     (used on the uni surface, where we have no per-employer tag
//     and want to infer the source from the uni's country).
export const SOURCE_META = {
  hesa_graduate_outcomes: {
    label: "HESA Graduate Outcomes",
    note: "UK's statutory graduate destinations survey (HESA / LEO).",
  },
  us_scorecard: {
    label: "US College Scorecard",
    note: "US Dept of Education programme-level outcomes dataset.",
  },
  cge_insertion: {
    label: "CGE Insertion Survey",
    note: "Conférence des Grandes Écoles graduate-insertion survey (FR).",
  },
  daad_absolventenbefragung: {
    label: "DAAD Absolventenbefragung",
    note: "German graduate-outcomes survey (DAAD / DZHW).",
  },
  duo_croho: {
    label: "DUO / CROHO reports",
    note: "Dutch Ministry of Education graduate-career dataset.",
  },
  hea_graduate_outcomes: {
    label: "HEA Graduate Outcomes",
    note: "Ireland's Higher Education Authority graduate-destinations survey.",
  },
  almalaurea: {
    label: "AlmaLaurea",
    note: "Italian universities' consortium graduate-career survey.",
  },
  dges_iefp: {
    label: "DGES / IEFP",
    note: "Portuguese Higher Education + employment agencies' outcomes data.",
  },
  editorial_spain: {
    label: "WUA editorial (Spain)",
    note: "WhichUniversityAdvisor hand-curated from firm grad pages and press.",
  },
  editorial_programme_page: {
    label: "WUA editorial (programme page)",
    note: "Pulled from the university's own programme page / career services.",
  },
  scb_universityadmissions: {
    label: "SCB + universityadmissions.se",
    note: "Swedish Statistics Agency + central admissions portal.",
  },
  uae_firm_hubs: {
    label: "WUA UAE firm hubs",
    note: "WhichUniversityAdvisor UAE employer-hub seed (UAE has no public GDS).",
  },
};

export function labelForTag(tag) {
  if (!tag) return null;
  return SOURCE_META[tag] || { label: String(tag).replace(/_/g, " "), note: "Internal data tag." };
}

// Country code / name → canonical source tag. Mirrors
// `scripts/backfill_employer_edges_source.py:_SOURCES` so the uni page
// shows the same label the backfill stamped onto its degrees.
const _COUNTRY_TO_TAG = {
  uk: "hesa_graduate_outcomes",
  us: "us_scorecard",
  fr: "cge_insertion",
  de: "daad_absolventenbefragung",
  nl: "duo_croho",
  ie: "hea_graduate_outcomes",
  ch: "editorial_programme_page",
  es: "editorial_spain",
  pt: "dges_iefp",
  it: "almalaurea",
  se: "scb_universityadmissions",
  hk: "editorial_programme_page",
  ae: "uae_firm_hubs",
};
const _NAME_TO_CODE = {
  "united kingdom": "uk", "great britain": "uk", "england": "uk",
  "scotland": "uk", "wales": "uk", "northern ireland": "uk",
  "united states": "us", "united states of america": "us", "usa": "us",
  "united arab emirates": "ae", "uae": "ae",
  "sweden": "se",
  "hong kong": "hk", "hong kong sar": "hk",
  "ireland": "ie",
  "netherlands": "nl", "the netherlands": "nl", "holland": "nl",
  "germany": "de",
  "france": "fr",
  "italy": "it",
  "spain": "es",
  "portugal": "pt",
  "switzerland": "ch",
};

export function tagForCountry(country) {
  if (!country) return null;
  const k = String(country).toLowerCase().trim();
  if (_COUNTRY_TO_TAG[k]) return _COUNTRY_TO_TAG[k];
  const code = _NAME_TO_CODE[k];
  return code ? _COUNTRY_TO_TAG[code] || null : null;
}
